/**
 * The sessions and links Quies's feature page fixes for each home language. Every session is an existing sample
 * (`data/session-samples.json`); a language that has fewer than three shows what it has. No viewer is read.
 */
import type {ProofContent, SessionCard} from '@core/proof/model'
import {languageName} from '@product/quies/data/languages'
import {LOCAL_PAGES} from '@product/quies/data/local-pages/registry'
import {SESSION_SAMPLES, webLanguageTag, type SessionSample} from '@product/quies/data/session-samples'
import {walkthroughContent} from '@product/quies/data/walkthrough-content'
import {HOME_LANGS, type HomeLang} from '@product/quies/lib/homeLangs'
import {HREFLANG_MAP} from '@product/quies/lib/hreflang'
import {quiesCopy, WALKTHROUGH_REF} from './spec'

/** English leads with these, in this order. */
const ENGLISH_LEAD = ['hypnosis-for-sleep', 'hypnosis-for-anxiety', 'hypnosis-for-stress']
export const HERO_SESSIONS = 3

const primaryOf = (sample: SessionSample) => webLanguageTag(sample.inLanguage).split('-')[0]

const cardOf = (id: string, sample: SessionSample): SessionCard => ({
    id,
    title: sample.title,
    writtenFor: sample.description,
    lang: webLanguageTag(sample.inLanguage),
    langName: languageName(primaryOf(sample)),
    seconds: sample.durationSeconds,
    src: sample.contentUrl,
    transcript: sample.transcript,
})

/** The samples spoken in a language: English's leads first, then the file's order. */
export function sessionsIn(language: string): SessionCard[] {
    const ids = Object.keys(SESSION_SAMPLES).filter((id) => primaryOf(SESSION_SAMPLES[id]) === language)
    const rank = (id: string) => (language === 'en' && ENGLISH_LEAD.includes(id) ? ENGLISH_LEAD.indexOf(id) : ENGLISH_LEAD.length)
    return ids.sort((a, b) => rank(a) - rank(b)).map((id) => cardOf(id, SESSION_SAMPLES[id]))
}

/** One session for each language other than `lang`: the homes' languages first, then the rest as the file holds them. */
export function sessionsInOtherLanguages(lang: HomeLang): SessionCard[] {
    const spoken = [...new Set(Object.values(SESSION_SAMPLES).map(primaryOf))]
    const order = [...HOME_LANGS, ...spoken.filter((l) => !(HOME_LANGS as readonly string[]).includes(l))]
    return order.filter((l) => l !== lang && spoken.includes(l)).map((l) => sessionsIn(l)[0])
}

type Method = keyof ReturnType<typeof quiesCopy>['methods']['items']
const METHODS: Method[] = ['ericksonian', 'autogenic', 'pmr', 'nidra', 'nsdr', 'sophrology']

/** Each method's pages, the first being the one a reader without a page in their language is sent to. */
const METHOD_PAGES: Record<Method, string[]> = {
    ericksonian: [HREFLANG_MAP.ericksonian.en],
    autogenic: ['/autogenes-training', '/jiritsu-kunren-ho', '/training-autogeno', '/trening-autogenny'],
    pmr: ['/progressive-muscle-relaxation', '/relajacion-progresiva-de-jacobson'],
    nidra: ['/yoga-nidra'],
    nsdr: ['/nsdr'],
    sophrology: ['/sophrologie'],
}

/** Chinese and Japanese readers read each other's pages. */
const READS: Record<string, string[]> = {zh: ['zh', 'ja'], ja: ['ja', 'zh']}

const pageLang = (path: string) => Object.values(LOCAL_PAGES).find((page) => page.path === path)?.lang

export function methodPath(lang: HomeLang, method: Method): string {
    const pages = METHOD_PAGES[method]
    for (const language of READS[lang] ?? [lang]) {
        const own = pages.find((path) => pageLang(path) === language)
        if (own) return own
    }
    return pages[0]
}

export function quiesContent(lang: HomeLang): ProofContent {
    const copy = quiesCopy(lang)
    const own = sessionsIn(lang)
    const frames = walkthroughContent.generic[lang]!.frames
    return {
        sheet: [],
        shownOn: null,
        chips: [],
        hero: {kind: 'sessions', sessions: own.slice(0, HERO_SESSIONS)},
        visuals: [
            {kind: 'steps', ref: WALKTHROUGH_REF, steps: frames.map(({title, description}) => ({title, body: description}))},
            {kind: 'listen', sessions: own.slice(0, HERO_SESSIONS)},
            {kind: 'sessions', sessions: sessionsInOtherLanguages(lang)},
            {kind: 'note', lines: copy.remembers.lines},
            {
                kind: 'links',
                items: METHODS.map((method) => ({
                    label: copy.methods.items[method].label,
                    body: copy.methods.items[method].desc,
                    to: methodPath(lang, method),
                })),
            },
        ],
    }
}
